import { h } from 'preact';
import { KEYBINDING_ACTIONS, RADIAL_ACTIONS } from '../../utils/game/keybindings';
import { GOLD, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { t } from '../../localization/t';

// 8-slice radial menu opened by RT-hold. The parent drives it:
//
//   - `visible`. RT is held
//   - `centerX`, `centerY`. Screen-space centre (reticle position)
//   - `selectedIndex`. Currently highlighted slice, or -1 for
//                           neutral (stick near centre)
//
// Commit-on-release is the parent's job: when RT goes up, the parent
// reads the last `selectedIndex` and dispatches the corresponding
// action, then hides the menu.
//
// The slice count is hardcoded to RADIAL_ACTIONS.length so the visual
// and the dispatch table can never drift. RADIAL_ACTIONS is the
// ordering authority. Edit it to change the menu.

const OUTER_RADIUS = 120;
const INNER_RADIUS = 50;
const SLICE_COUNT = RADIAL_ACTIONS.length;
const ACTIONS_BY_ID = new Map(KEYBINDING_ACTIONS.map((a) => [a.id, a]));

// Compute the angle (radians, CCW from +x) for the centre of slice i,
// with slice 0 at 12 o'clock. Also returns the text-anchor x/y in the
// menu's local coordinates (centre = 0,0).
function sliceGeometry(i) {
  const angleFrom12 = (i / SLICE_COUNT) * Math.PI * 2;
  const angle = -Math.PI / 2 + angleFrom12;
  const labelR = (OUTER_RADIUS + INNER_RADIUS) / 2;
  return {
    angle,
    labelX: Math.cos(angle) * labelR,
    labelY: Math.sin(angle) * labelR,
    startAngle: angle - Math.PI / SLICE_COUNT,
    endAngle: angle + Math.PI / SLICE_COUNT,
  };
}

function sliceWedgePath(i) {
  const { startAngle, endAngle } = sliceGeometry(i);
  const x0 = Math.cos(startAngle) * INNER_RADIUS;
  const y0 = Math.sin(startAngle) * INNER_RADIUS;
  const x1 = Math.cos(startAngle) * OUTER_RADIUS;
  const y1 = Math.sin(startAngle) * OUTER_RADIUS;
  const x2 = Math.cos(endAngle) * OUTER_RADIUS;
  const y2 = Math.sin(endAngle) * OUTER_RADIUS;
  const x3 = Math.cos(endAngle) * INNER_RADIUS;
  const y3 = Math.sin(endAngle) * INNER_RADIUS;
  return [
    `M ${x0} ${y0}`,
    `L ${x1} ${y1}`,
    `A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 0 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${INNER_RADIUS} ${INNER_RADIUS} 0 0 0 ${x0} ${y0}`,
    'Z',
  ].join(' ');
}

// Pick the slice that the current right-stick vector points at. Stick
// values in the dead zone produce `-1` (no selection). Exported so the
// GameBoard integration can reuse the same logic when RT is released.
export function selectedSliceForStick(stickX, stickY, deadZone = 0.35) {
  const mag = Math.hypot(stickX, stickY);
  if (mag < deadZone) return -1;
  // Convert to "angle from 12 o'clock, clockwise", which matches the
  // slice ordering in RADIAL_ACTIONS.
  let angle = Math.atan2(stickY, stickX) + Math.PI / 2;
  if (angle < 0) angle += Math.PI * 2;
  const slice = Math.floor((angle / (Math.PI * 2)) * SLICE_COUNT + 0.5) % SLICE_COUNT;
  return slice;
}

export function RadialActionMenu({ visible, centerX, centerY, selectedIndex = -1 }) {
  if (!visible) return null;
  const size = OUTER_RADIUS * 2 + 16;
  const half = size / 2;
  return h(
    'div',
    {
      'aria-hidden': 'true',
      style: {
        position: 'fixed',
        left: `${centerX - half}px`,
        top: `${centerY - half}px`,
        width: `${size}px`,
        height: `${size}px`,
        pointerEvents: 'none',
        zIndex: 9999,
      },
    },
    h(
      'svg',
      { width: size, height: size, viewBox: `${-half} ${-half} ${size} ${size}` },
      // Backdrop ring.
      h('circle', {
        cx: 0, cy: 0, r: OUTER_RADIUS,
        fill: 'rgba(0,0,0,0.55)', stroke: `${GOLD} 0.3)`, 'stroke-width': 1,
      }),
      ...RADIAL_ACTIONS.map((actionId, i) => {
        const action = ACTIONS_BY_ID.get(actionId);
        const isSelected = i === selectedIndex;
        const fill = isSelected ? `${GOLD} 0.28)` : 'rgba(255,255,255,0.04)';
        const stroke = isSelected ? `${GOLD} 0.9)` : 'rgba(255,255,255,0.12)';
        return h(
          'g',
          { key: actionId },
          h('path', {
            d: sliceWedgePath(i),
            fill, stroke, 'stroke-width': isSelected ? 2 : 1,
          }),
        );
      }),
      // Labels in a separate pass so they sit on top of every wedge.
      ...RADIAL_ACTIONS.map((actionId, i) => {
        const action = ACTIONS_BY_ID.get(actionId);
        const { labelX, labelY } = sliceGeometry(i);
        const label = shortLabel(action?.label ? t(action.label) : actionId);
        return h(
          'text',
          {
            key: `${actionId}-label`,
            x: labelX, y: labelY,
            'text-anchor': 'middle',
            'dominant-baseline': 'middle',
            fill: TEXT_PRIMARY,
            'font-size': '12',
            'font-weight': i === selectedIndex ? 600 : 400,
            style: { pointerEvents: 'none' },
          },
          label,
        );
      }),
      // Inner hole.
      h('circle', {
        cx: 0, cy: 0, r: INNER_RADIUS,
        fill: 'rgba(0,0,0,0.75)', stroke: `${GOLD} 0.2)`, 'stroke-width': 1,
      }),
      h('text', {
        x: 0, y: 0,
        'text-anchor': 'middle', 'dominant-baseline': 'middle',
        fill: TEXT_PRIMARY, 'font-size': '11', opacity: 0.7,
      }, selectedIndex === -1 ? t('Flick') : ''),
    ),
  );
}

function shortLabel(full) {
  if (!full) return '';
  if (full.length <= 16) return full;
  const firstSlash = full.indexOf(' / ');
  if (firstSlash !== -1) return full.slice(0, firstSlash);
  return `${full.slice(0, 14)}\u2026`;
}

export default RadialActionMenu;
